import { ThemeProps } from 'styled-components';
import { Breakpoint, Theme } from '../theme';

/**
 * This helper function calculates an item of grid width `childItemWidthInColumns` when the GridItem component is
 * not appropriate. It requires the parent container to be `parentGridItemWidthInColumns` wide to work.
 *
 * @param parentGridItemWidthInColumns
 * @param childItemWidthInColumns
 * @param borderBoxSpacing the side non-content width spacing in the border box, ie. (left padding + left border size). This
 *                          assumes that the spacing on both sides are the same. A refactor is needed for the case where they
 *                          are different.
 * @param breakpoint
 * @param isNestedCalc If this is being used inside a calc statement this boolean should be set to true as IE does not not support
 *                      nested calc statements.
 */
export const calculateGridItemChildWidth = (
  parentGridItemWidthInColumns: number,
  childItemWidthInColumns: number,
  breakpoint: Breakpoint,
  borderBoxSpacing?: string,
  isNestedCalc?: boolean
) => ({ theme }: ThemeProps<Theme>): string => {
  const widthProportion = childItemWidthInColumns / parentGridItemWidthInColumns;
  /*  Let `n` be the desired column width, `g` be gutter size, `s` be side spacing (padding + border; e.g. spacing(4)),
      `p` = n / 10 the widthProportion (since our container is 10 columns wide). Then, from this component
      we have:
        // We have to add 2 * s because the parent width is only the width of the content-box
        // and not the entire border-box
        tenColWidth = 100% + 2 * s + g = 100% + 2s + g
        desiredColWidthIncludingGutter = tenColWidth * widthProportion
        desiredColWidthExcludingGutter = desiredColWidthIncludingGutter - g
        actualContentWidth = desiredColWidthExcludingGutter - s  // there is `s` spacing to the left
                            = (100% + 2s + g) * widthProportion - g - s
                            = (100% * widthProportion) - (1 - widthProportion) * g + (2 * widthProportion - 1) * s
    */
  return `${isNestedCalc ? '' : 'calc'}((${widthProportion * 100}% - ${
    (1 - widthProportion) * theme.layout[breakpoint].gutterPx
  }px) + (${borderBoxSpacing ?? '0px'} * ${2 * widthProportion - 1}))`;
};
